import { useEffect, useRef } from 'react';
import { TOUCH_LAYOUT_QUERY } from './useTouchLayout.ts';

/**
 * Hook that traps keyboard focus within a container element.
 * When active, Tab and Shift+Tab cycle through focusable elements inside the container.
 * Focus is moved to the first focusable element on mount and restored on unmount.
 */
export function useFocusTrap(active: boolean = true) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;

    // Save currently focused element to restore later
    previousFocusRef.current = document.activeElement as HTMLElement;

    const container = containerRef.current;
    if (!container) return;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    // Focus the first visible focusable element (or the container itself). #1828 — een element
    // met `data-autofocus` gaat voor (bv. de aanbevolen keuze in ModalDialogs); anders kreeg de
    // sluitknop in de kop de focus en ging de bedoelde standaardfocus verloren.
    // #1836 — op touch (telefoon, tablet) niet de eerste knop: dat is meestal de sluitknop, die
    // dan een focusring kreeg zonder dat iemand een toets gebruikte. Daar krijgt het venster zelf
    // de focus (geen ring, een schermlezer leest het venster voor); Tab gaat daarna naar binnen.
    const touch = !!window.matchMedia?.(TOUCH_LAYOUT_QUERY)?.matches;
    const focusFirst = () => {
      // #1836 — de eerste focus wacht 50 ms op het tekenen. Kreeg intussen iets in het venster
      // zelf al bewust de focus (de eerste keuze in de conflictbalk, een autofocus-veld), dan
      // blijft die staan: anders pakte deze timer hem weer af, afhankelijk van de timing.
      const nu = document.activeElement;
      if (nu && nu !== container && container.contains(nu)) return;
      const first = Array.from(
        container.querySelectorAll<HTMLElement>(`[data-autofocus], ${focusableSelector}`)
      )
        .filter((el) => el.offsetParent !== null)
        .filter((el) => !touch || el.hasAttribute('data-autofocus'))
        .sort(
          (a, b) =>
            Number(b.hasAttribute('data-autofocus')) - Number(a.hasAttribute('data-autofocus'))
        )[0];
      if (first) first.focus();
      else {
        if (!container.hasAttribute('tabindex')) container.tabIndex = -1;
        container.setAttribute('data-focus-trap', '');
        container.focus();
      }
    };

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(focusFirst, 50);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = Array.from(
        container.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter((el: HTMLElement) => el.offsetParent !== null); // Exclude hidden elements

      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        // Shift+Tab: wrap to last element
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: wrap to first element
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timer);
      container.removeEventListener('keydown', handleKeyDown);
      // Restore previous focus
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [active]);

  return containerRef;
}
